import HomepageHeroSliderImage from '@components/HomepageHeroSliderImage'
import { Container } from '@components/gassan-ui'
import { Button } from '@components/rolex-ui/Button'
import { Heading } from '@components/rolex-ui/Heading'
import { cn } from '@lib/cn'
import { RolexPromoBanner as RolexPromoBannerModel } from '@lib/kontent/models/RolexPromoBanner'
import { FC } from 'react'

type RolexPromoBannerProps = {
  item: RolexPromoBannerModel
  variant?: 'default' | 'hero' | 'in-grid'
  className?: string
  slideToPreload?: RolexPromoBannerModel
}

export const RolexPromoBanner: FC<RolexPromoBannerProps> = ({
  item,
  variant = 'default',
  className,
  slideToPreload,
}) => {
  const alignLeft = item.align_content.value[0]?.codename === 'left'
  const colorIsWhite = item.text_color.value[0]?.codename === 'light'

  return (
    <section className={cn('relative', className)}>
      <div
        key={item.title.value}
        className={cn(
          'relative mx-auto w-full max-w-[2560px] overflow-hidden',
          'before:content before:block',
          getHeightClasses(variant),
        )}
      >
        <HomepageHeroSliderImage {...item} slideToPreload={slideToPreload || item} />
      </div>
      <Container
        className={cn(
          'absolute left-0 right-0 z-[2] grid grid-cols-6 items-center md:top-1/2 md:!w-[calc(100%-10rem)] md:-translate-y-1/2 md:grid-cols-12 xl:!w-[calc(100%-14rem)]',
          getBottomClasses(variant),
        )}
      >
        <div
          className={cn(
            'flex flex-col items-start text-rolexBlack',
            getColSpanClasses(variant),
            !alignLeft && 'md:col-start-7',
            colorIsWhite && 'text-white',
          )}
        >
          <Heading variant="headline18">{item.pretitle.value}</Heading>
          <Heading variant={variant === 'hero' ? 'headline70' : 'headline36'} className="mb-4">
            {item.title.value}
          </Heading>
          <Button as="link" variant="primary" href={item.button_link.value}>
            {item.button_text.value}
          </Button>
        </div>
      </Container>
    </section>
  )
}

function getHeightClasses(variant: RolexPromoBannerProps['variant']) {
  switch (variant) {
    case 'hero':
      return 'before:h-[500px] md:before:h-[600px] lg:before:h-[650px] xl:before:h-[700px] xxl:before:h-[725px]'
    case 'in-grid':
      return 'before:h-[200px] md:before:h-[300px] lg:before:h-[300px] xl:before:h-[350px]'
    default:
      return 'before:h-[300px] md:before:h-[350px] lg:before:h-[400px] xl:before:h-[450px]'
  }
}

function getBottomClasses(variant: RolexPromoBannerProps['variant']) {
  switch (variant) {
    case 'hero':
      return 'bottom-24 md:bottom-16'
    case 'in-grid':
      return 'bottom-6 md:bottom-0'
    default:
      return 'bottom-16 md:bottom-8'
  }
}

function getColSpanClasses(variant: RolexPromoBannerProps['variant']) {
  switch (variant) {
    case 'hero':
      return 'col-span-6'
    case 'in-grid':
      return 'col-span-6'
    default:
      return 'col-span-4'
  }
}
